import { useCallback, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { parse } from 'date-fns/parse';
import { useTranslation } from 'react-i18next';
import { object, number, string } from 'yup';
import { App, Input, message, Table, Typography } from 'antd';
import { PaymentHttp, UserHttp } from '@/services';
import { useAppStore } from '@/store';
import { AppButton, Flex, InputNumeric } from '@/components';
import { format as formatDate } from 'date-fns-jalali/format';
import { useWalletPageContext } from './WalletPageContext';

export interface TransferCreditProps {
  isSubsidy: boolean;
  transferInfo: PaymentHttp.TransferInfo;
}

const INPUT_STYLE = {
  fontSize: '0.875rem',
  border: '2px solid #d9d8d8',
};

function resolveGenderTitle(gender: string | undefined, t: (key: string) => string) {
  if (!gender) return '';
  const normalized = gender.trim().toLowerCase();
  if (normalized === 'مرد' || normalized === 'male') {
    return t('payment:genderTitle.male');
  }
  if (normalized === 'زن' || normalized === 'female') {
    return t('payment:genderTitle.female');
  }
  return '';
}

export function TransferCredit({ isSubsidy, transferInfo }: TransferCreditProps) {

  const [t] = useTranslation(['common', 'payment']);
  const { modal } = App.useApp();
  const { hasSubsidy } = useWalletPageContext();
  const [submitted, setSubmitted] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [fetchCredit, subsidies] = useAppStore(state => [state.fetchCredit, state.subsidies]);

  const selectedSubsidy = useMemo(() => {
    return subsidies.find((_, index) => index === selectedRowKey);
  }, [subsidies, selectedRowKey]);

  const limits = useMemo(() => {
    if (isSubsidy) {
      if (!selectedSubsidy) {
        return { min: 0, max: 0 };
      }
      return {
        min: 1,
        max: Math.floor((selectedSubsidy?.credit || 0) / 10),
      };
    }
    if (!transferInfo) {
      return { min: 0, max: undefined as number | undefined };
    }
    return {
      min: Math.floor(transferInfo.minCreditTransferAmount / 10),
      max: Math.floor(transferInfo.maxCreditTransferAmount / 10),
    };
  }, [isSubsidy, selectedSubsidy, transferInfo]);

  const validationSchema = useMemo(() => object({
    nurtureProfileId: number().integer().nullable().default(null),
    username: string().required().min(3).label(t('common:labels.username')),
    amount: number()
      .required()
      .integer()
      .positive()
      .min(Math.max(limits.min, 1))
      .max(limits.max ?? Number.MAX_SAFE_INTEGER)
      .label(t('payment:labels.amount'))
  }), [limits.min, limits.max, t]);

  const formik = useFormik({
    validationSchema,
    initialValues: {
      username: '',
      amount: undefined as number,
      nurtureProfileId: null
    },
    onSubmit: () => {},
  });

  const executeTransfer = useCallback(async ({
    username,
    amount,
    nurtureProfileId,
  }: {
    username: string;
    amount: number;
    nurtureProfileId: number | null;
  }) => {
    try {
      await PaymentHttp.transferCredit({ username, amount, nurtureProfileId });
      setSubmitted(false);
      formik.resetForm();
      fetchCredit();
      message.success(t('payment:messages.success-transfer-credit'));
    }
    catch (e) {
    }
  }, [fetchCredit, formik, t]);

  const buildConfirmContent = useCallback(async (username: string) => {
    const info = await UserHttp.fetchMobileUserInfo(username);
    if (info) {
      const title = resolveGenderTitle(info.gender, t);
      return t('payment:messages.confirm-transfer-credit-with-name', {
        title,
        firstName: info.firstName,
        lastName: info.lastName,
      });
    }
    return t('payment:messages.confirm-transfer-credit-with-username', { username });
  }, [t]);

  const handleTransferClick = useCallback(async () => {
    setSubmitted(true);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      return;
    }

    const { username, amount, nurtureProfileId } = formik.values;
    if (amount == null) {
      return;
    }

    setConfirmLoading(true);
    try {
      const content = await buildConfirmContent(username.trim());
      modal.confirm({
        type: 'warn',
        okText: t('common:labels.yes'),
        cancelText: t('common:labels.no'),
        content,
        onOk: () => executeTransfer({
          username: username.trim(),
          amount,
          nurtureProfileId,
        }),
      });
    }
    finally {
      setConfirmLoading(false);
    }
  }, [buildConfirmContent, executeTransfer, formik, modal, t]);

  return (
    <Flex direction="column" className="flex-grow">
      {isSubsidy && (
        hasSubsidy && subsidies.length > 0 &&
        <Flex direction="column" className="w-full rounded text-sm">
          <Table
            size="small"
            className="!text-sm rounded"
            pagination={false}
            rowSelection={{
              type: 'radio',
              onChange: ([selectedRowKey]) => {
                setSelectedRowKey(selectedRowKey);
                const subsidy = subsidies.find((_, index) => index === selectedRowKey);
                if (!subsidy) return;
                formik.setFieldValue('amount', (subsidy?.credit || 0) / 10);
                formik.setFieldValue('nurtureProfileId', subsidy?.id || 0);
              }
            }}
            columns={[
              { dataIndex: 'index', title: t('common:labels.id'), width: '50px' },
              { dataIndex: 'credit', title: t('common:labels.credit') },
              { dataIndex: 'expiration', title: t('common:labels.expiration') },
            ]}
            dataSource={(subsidies ?? []).map((item, index) => {
              const date = parse(item?.expirationDate, 'yyyy-MM-dd HH:mm:ss', new Date());
              const formattedDate = formatDate(date, 'yyyy/MM/dd');
              return {
                key: index,
                index: index + 1,
                credit: (item?.credit || 0) / 10,
                expiration: formattedDate
              }
            })}
          />
        </Flex>
      )}
      {!isSubsidy && (
        <div className="py-3 px-2 leading-normal text-start text-nurture-darker text-sm mb-0">
          <ul className="!list-disc my-0" style={{ marginInlineStart: '-2rem' }}>
            {transferInfo?.messages.map((msg, i) => <li key={i}>{msg}</li>)}
          </ul>
        </div>
      )}
      <Flex direction="column" gap={10} className="mt-auto">
        <Input
          size="large"
          name="username"
          style={INPUT_STYLE}
          onChange={formik.handleChange}
          value={formik.values.username}
          disabled={isSubsidy && selectedRowKey == null}
          placeholder={t('payment:labels.username-credit-recipient')}
        />
        <InputNumeric
          size="large"
          style={INPUT_STYLE}
          value={formik.values.amount}
          min={limits.min}
          max={limits.max}
          disabled={(isSubsidy && selectedRowKey == null) || (!isSubsidy && !transferInfo)}
          onChange={value => formik.setFieldValue('amount', value == null ? undefined : value)}
          onBlur={formik.handleBlur}
          placeholder={t('payment:labels.amount-toman-min', { min: Math.max(limits.min, 1) })}
        />
        {submitted && Object.keys(formik.errors).length > 0 &&
          <Typography.Text className="text-xs" type="danger">
            {formik.errors[Object.keys(formik.errors)[0]]}
          </Typography.Text>
        }

        <AppButton
          size="middle"
          appVariant="submit"
          loading={confirmLoading}
          disabled={isSubsidy && selectedRowKey == null}
          onClick={handleTransferClick}
        >
          {t('payment:labels.transfer-credit')}
        </AppButton>
      </Flex>
    </Flex>
  );
};

export default TransferCredit;
